import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { ArrowLeft, Trophy, CurrencyDollar, Users, Lightning, Crown, CalendarDots, MapPin, Plus, Storefront } from '@phosphor-icons/react';
import { useAuth } from '../contexts/AuthContext';
import BottomNav from '../components/BottomNav';
import GamePill from '../components/GamePill';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function SponsoredTournamentsPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [tournaments, setTournaments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [statusFilter, setStatusFilter] = useState('');
  const [form, setForm] = useState({
    name: '', game_type: 'Chess', date: '', max_participants: 16,
    entry_fee: 5, prize_description: '', sponsor_name: '', sponsor_logo: '',
  });

  useEffect(() => { fetchTournaments(); }, [statusFilter]);

  const fetchTournaments = async () => {
    try {
      const params = statusFilter ? { status: statusFilter } : {};
      const { data } = await axios.get(`${API_URL}/api/sponsored-tournaments`, { params, withCredentials: true });
      setTournaments(data);
    } catch (err) {
      toast.error('Failed to load tournaments');
    } finally { setLoading(false); }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.name || !form.date) return toast.error('Name and date required');
    try {
      await axios.post(`${API_URL}/api/sponsored-tournaments`, form, { withCredentials: true });
      toast.success('Sponsored tournament created!');
      setShowCreate(false);
      setForm({ name: '', game_type: 'Chess', date: '', max_participants: 16, entry_fee: 5, prize_description: '', sponsor_name: '', sponsor_logo: '' });
      fetchTournaments();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create');
    }
  };

  const handleRegister = async (id) => {
    try {
      const { data } = await axios.post(`${API_URL}/api/sponsored-tournaments/${id}/register`, {}, { withCredentials: true });
      toast.success(data.message);
      fetchTournaments();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Registration failed');
    }
  };

  if (loading) return <div className="app-container flex items-center justify-center min-h-screen"><div className="w-10 h-10 border-4 border-[#9D4CDD] border-t-transparent rounded-full animate-spin" /></div>;

  const GAME_TYPES = ['Chess', 'D&D', 'Catan', 'Magic: The Gathering', 'Pokemon', 'Yu-Gi-Oh!', 'Warhammer'];

  return (
    <div className="app-container" data-testid="sponsored-tournaments-page">
      <header className="p-6 pb-3">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate(-1)} className="w-10 h-10 rounded-full bg-[#181824] flex items-center justify-center" data-testid="sponsored-back-button">
              <ArrowLeft size={20} weight="bold" className="text-white" />
            </button>
            <div>
              <h1 className="text-xl font-bold text-white flex items-center gap-2" style={{ fontFamily: 'Unbounded, sans-serif' }}>
                <Crown size={24} weight="duotone" className="text-[#FACC15]" />
                Sponsored
              </h1>
              <p className="text-[#71717A] text-xs">Prize pool tournaments</p>
            </div>
          </div>
          <button onClick={() => setShowCreate(!showCreate)}
            className="w-10 h-10 rounded-full bg-[#9D4CDD] flex items-center justify-center" data-testid="create-sponsored-btn">
            <Plus size={20} weight="bold" className="text-white" />
          </button>
        </div>
      </header>

      <div className="px-6 pb-32 space-y-4">
        {/* Create Form */}
        {showCreate && (
          <motion.form initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }}
            onSubmit={handleCreate} className="p-4 rounded-xl bg-[#0F0F16] border border-[#9D4CDD]/20 space-y-3" data-testid="create-sponsored-form">
            <input value={form.name} onChange={e => setForm(f => ({...f, name: e.target.value}))} placeholder="Tournament Name"
              className="w-full p-3 rounded-xl bg-[#181824] border border-white/5 text-white text-sm placeholder-[#71717A] outline-none focus:border-[#9D4CDD]/50" data-testid="sponsored-name-input" />
            <select value={form.game_type} onChange={e => setForm(f => ({...f, game_type: e.target.value}))}
              className="w-full p-3 rounded-xl bg-[#181824] border border-white/5 text-white text-sm outline-none" data-testid="sponsored-game-select">
              {GAME_TYPES.map(g => <option key={g} value={g}>{g}</option>)}
            </select>
            <input type="date" value={form.date} onChange={e => setForm(f => ({...f, date: e.target.value}))}
              className="w-full p-3 rounded-xl bg-[#181824] border border-white/5 text-white text-sm outline-none" data-testid="sponsored-date-input" />
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-[#71717A] text-[10px] mb-1 block">Max Players</label>
                <input type="number" value={form.max_participants} onChange={e => setForm(f => ({...f, max_participants: parseInt(e.target.value) || 16}))}
                  className="w-full p-3 rounded-xl bg-[#181824] border border-white/5 text-white text-sm outline-none" data-testid="sponsored-max-input" />
              </div>
              <div>
                <label className="text-[#71717A] text-[10px] mb-1 block">Entry Fee ($)</label>
                <input type="number" step="0.5" value={form.entry_fee} onChange={e => setForm(f => ({...f, entry_fee: parseFloat(e.target.value) || 5}))}
                  className="w-full p-3 rounded-xl bg-[#181824] border border-white/5 text-white text-sm outline-none" data-testid="sponsored-fee-input" />
              </div>
            </div>
            <input value={form.prize_description} onChange={e => setForm(f => ({...f, prize_description: e.target.value}))} placeholder="Prize Description"
              className="w-full p-3 rounded-xl bg-[#181824] border border-white/5 text-white text-sm placeholder-[#71717A] outline-none" data-testid="sponsored-prize-input" />
            <input value={form.sponsor_name} onChange={e => setForm(f => ({...f, sponsor_name: e.target.value}))} placeholder="Sponsor Name (optional)"
              className="w-full p-3 rounded-xl bg-[#181824] border border-white/5 text-white text-sm placeholder-[#71717A] outline-none" data-testid="sponsored-sponsor-input" />
            <button type="submit" className="btn-primary w-full text-sm" data-testid="submit-sponsored-btn">Create Sponsored Tournament</button>
          </motion.form>
        )}

        {/* Status Filters */}
        <div className="flex gap-2">
          {['', 'registration', 'in_progress', 'completed'].map(s => (
            <button key={s} onClick={() => setStatusFilter(s)}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-all ${statusFilter === s ? 'bg-[#9D4CDD] text-white' : 'bg-[#181824] text-[#71717A] hover:text-white'}`}
              data-testid={`filter-${s || 'all'}`}>
              {s === '' ? 'All' : s === 'registration' ? 'Open' : s === 'in_progress' ? 'Live' : 'Done'}
            </button>
          ))}
        </div>

        {/* Tournaments List */}
        <div className="space-y-3" data-testid="sponsored-list">
          {tournaments.length === 0 ? (
            <div className="text-center py-12">
              <Trophy size={48} weight="duotone" className="text-[#71717A] mx-auto mb-3" />
              <p className="text-[#71717A] text-sm">No sponsored tournaments yet.</p>
              <p className="text-[#71717A] text-xs mt-1">Create one to get started!</p>
            </div>
          ) : tournaments.map((t, i) => {
            const isRegistered = t.participants?.some(p => p.id === user?.id);
            const isFull = t.current_participants >= t.max_participants;
            const statusColors = { registration: 'text-[#39FF14] bg-[#39FF14]/10', in_progress: 'text-[#FACC15] bg-[#FACC15]/10', completed: 'text-[#71717A] bg-[#71717A]/10' };
            const sc = statusColors[t.status] || statusColors.registration;
            return (
              <motion.div key={t.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.06 }}
                className="p-4 rounded-xl bg-[#0F0F16] border border-white/5 hover:border-white/10 transition-all"
                data-testid={`sponsored-tournament-${t.id}`}>
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <h3 className="text-white text-sm font-bold">{t.name}</h3>
                    <div className="flex items-center gap-2 mt-1 flex-wrap">
                      <GamePill game={t.game_type} small />
                      <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${sc}`}>
                        {t.status === 'registration' ? 'Open' : t.status === 'in_progress' ? 'Live' : 'Completed'}
                      </span>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-[#FACC15] text-sm font-bold">${(t.prize_pool || 0).toFixed(0)}</p>
                    <p className="text-[#71717A] text-[10px]">Prize Pool</p>
                  </div>
                </div>

                <div className="flex items-center gap-4 text-[#71717A] text-xs mb-3">
                  <div className="flex items-center gap-1"><CalendarDots size={14} /><span>{t.date}</span></div>
                  <div className="flex items-center gap-1"><Users size={14} /><span>{t.current_participants}/{t.max_participants}</span></div>
                  <div className="flex items-center gap-1"><CurrencyDollar size={14} /><span>${t.entry_fee} entry</span></div>
                </div>

                {t.sponsor_name && (
                  <div className="flex items-center gap-1 mb-3">
                    <Storefront size={12} className="text-[#9D4CDD]" />
                    <span className="text-[#9D4CDD] text-[10px] font-medium">Sponsored by {t.sponsor_name}</span>
                  </div>
                )}

                {t.prize_description && <p className="text-[#A1A1AA] text-xs mb-3">{t.prize_description}</p>}

                {t.status === 'registration' && !isRegistered && !isFull && (
                  <button onClick={() => handleRegister(t.id)}
                    className="btn-primary w-full text-sm" data-testid={`register-sponsored-${t.id}`}>
                    Register (${t.entry_fee})
                  </button>
                )}
                {isRegistered && <p className="text-[#39FF14] text-xs text-center font-semibold">Registered</p>}
                {isFull && !isRegistered && <p className="text-[#71717A] text-xs text-center">Tournament Full</p>}
              </motion.div>
            );
          })}
        </div>
      </div>

      <BottomNav />
    </div>
  );
}
